import React from 'react';
import classnames from 'classnames';

import { Modal } from '@components/ui';
import { SearchFieldMobile, SearchResultsMobile } from '@components/search';
import { isIOS } from '@helpers/BrowserHelpers';
import { clearAllIosBodyScrollLocks, disableIosBodyScroll } from '@helpers/ScrollHelpers';

import { useBreakpoint } from '@hooks/useBreakpoint';

import styles from './SearchModal.module.scss';

interface Props {
  isOpen: boolean,
  onToggle: (isActive: boolean) => void
}

const SearchModal = (props: Props) => {
  const {
    isOpen,
    onToggle,
  } = props;

  // Refs

  const bodyRef = React.useRef(null);

  // Hooks

  const isLg = useBreakpoint('lg', 'up');
  const [isFocused, setIsFocused] = React.useState(false);
  const [currentScrollY, setCurrentScrollY] = React.useState(null);

  // Helpers

  const setIOSScrollFix = () => {
    // After modal has opened
    // scroll to top to avoid jump on search field focus
    setCurrentScrollY(window.scrollY);
    window.scrollTo(0, 1);
  };

  const resetIOSScrollFix = () => {
    // When modal start closing
    // restore scroll
    if (currentScrollY) {
      window.scrollTo(0, currentScrollY);
    }
    setCurrentScrollY(null);
  };

  // Effects

  React.useEffect(() => {
    if (isLg) {
      onToggle(false);
    }
  }, [isLg]);

  // Render

  return (
    <Modal
      bodyRef={(node) => bodyRef.current = node}
      isOpen={isOpen}
      //
      className={classnames(
        styles.root,
        { [styles.ios]: isIOS() },
        { [styles.focused]: isFocused }
      )}
      contentClassName={classnames(
        styles.content,
        { [styles.visible]: isOpen }
      )}
      innerClassName={classnames(
        styles.inner
      )}
      headerClassName={classnames(
        styles.header
      )}
      //
      withPadding={false}
      withCloseButton={false}
      breakpoint="lg"
      responsiveProps={{
        position: 'bottom',
        isFullScreen: true
      }}
      //
      title={(
        <SearchModalTitle
          onSearchFieldClose={() => {
            onToggle(false);
          }}
          onSearchFieldFocus={() => {
            setIsFocused(true);
          }}
          onSearchFieldBlur={() => {
            setTimeout(() => {
              setIsFocused(false);
            });
          }}
        />
      )}
      headerProps={{
        withBackButton: true,
        withCloseButton: false,
        responsiveProps: {
          withCloseButton: false,
        },
        onBack: () => onToggle(false)
      }}
      //
      onAfterOpen={() => {
        // IOS-only
        if (isIOS()) {
          disableIosBodyScroll(bodyRef?.current);
        }
      }}
      onAfterClose={() => {
        // IOS-only
        if (isIOS()) {
          clearAllIosBodyScrollLocks();
        }
      }}
      onContentOpen={() => {
        // IOS-only
        if (isIOS()) {
          setIOSScrollFix();
        }
      }}
      onContentClosing={() => {
        // IOS-only
        if (isIOS()) {
          resetIOSScrollFix();
        }
      }}
    >
      <SearchModalContent />
    </Modal>
  );
};

// Components

// --- Title

interface SearchModalTitleProps {
  onSearchFieldInit?: () => void,
  onSearchFieldClose: () => void,
  onSearchFieldFocus: () => void,
  onSearchFieldBlur: () => void
}

const SearchModalTitle = React.memo((props: SearchModalTitleProps) => {
  const {
    onSearchFieldInit = () => {},
    onSearchFieldClose = () => {},
    onSearchFieldFocus = () => {},
    onSearchFieldBlur = () => {},
  } = props;

  return (
    <div className={styles.searchBar}>
      <SearchFieldMobile
        withActions
        onInit={onSearchFieldInit}
        onClose={onSearchFieldClose}
        onFocus={onSearchFieldFocus}
        onBlur={onSearchFieldBlur}
      />
    </div>
  );
});

// --- Content

const SearchModalContent = React.memo(() => {
  return (
    <div className={styles.searchResults}>
      <SearchResultsMobile />
    </div>
  );
});

// Export

export default SearchModal;
